import { useEffect, useState } from "react";
import api, { eur } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Boxes, Search, Minus, Plus } from "lucide-react";
import { defaultVat } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WH_SORTS, sortWarehouse } from "@/lib/warehouseSort";

export function WarehousePicker({ open, onOpenChange, vatMode = "SK", defaultMargin = "", warehouseVatRate = 23, onAdd }) {
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [store, setStore] = useState("all");
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [margin, setMargin] = useState("0");
  const [priceMode, setPriceMode] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("wh_picker_vat") === "gross") ? "gross" : "net");
  const pickMode = (mode) => { setPriceMode(mode); try { localStorage.setItem("wh_picker_vat", mode); } catch { /* noop */ } };
  const [sel, setSel] = useState({});
  const [sort, setSort] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("wh_picker_sort")) || "created_desc");
  const pickSort = (v) => { setSort(v); try { localStorage.setItem("wh_picker_sort", v); } catch { /* noop */ } };

  useEffect(() => {
    if (!open) return;
    setLoading(true); setSel({}); setQ(""); setStore("all");
    setMargin(defaultMargin !== "" && defaultMargin != null ? String(defaultMargin) : "0");
    Promise.all([api.get("/warehouse"), api.get("/warehouses")])
      .then(([a, b]) => { setItems(a.data || []); setStores(b.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, defaultMargin]);

  const filtered = sortWarehouse(items, sort).filter((it) => {
    if (store !== "all" && it.warehouse_id !== store) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [it.name, it.order_number, it.supplier, it.ean].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });
  const freeOf = (it) => Math.max(0, Number(it.free_qty ?? it.qty ?? 0));
  const toggle = (it) => setSel((p) => { const n = { ...p }; if (n[it.id] != null) delete n[it.id]; else n[it.id] = Math.min(1, freeOf(it)) || 1; return n; });
  const setQty = (id, v) => setSel((p) => ({ ...p, [id]: v }));
  const stepQty = (it, dir) => {
    const step = it.divisible ? 0.1 : 1;
    const next = Math.min(freeOf(it), Math.max(step, Math.round((Number(sel[it.id] || 0) + dir * step) * 10) / 10));
    setQty(it.id, String(next));
  };
  const m = Number(margin || 0);
  const vr2 = Number(warehouseVatRate) || 23;
  const basePrice = (pp) => priceMode === "gross" ? Number(pp || 0) * (1 + vr2 / 100) : Number(pp || 0);
  const sellPrice = (pp) => Math.round(basePrice(pp) * (1 + m / 100) * 100) / 100;
  const selectedCount = Object.keys(sel).length;

  const confirm = () => {
    const vr = defaultVat(vatMode);
    const rows = items.filter((it) => sel[it.id] != null).map((it) => ({
      description: it.name, kind: "material",
      qty: Number(sel[it.id]) || 1, unit: it.divisible ? "l" : (it.unit || "ks"),
      unit_price: sellPrice(it.purchase_price), discount: 0, vat_rate: vr,
      stock_item_id: it.id,
    }));
    onAdd?.(rows);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-3xl" data-testid="warehouse-picker-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading uppercase"><Boxes className="h-5 w-5 text-primary" /> Výber zo skladu</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="warehouse-picker-stores">
          <button onClick={() => setStore("all")} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${store === "all" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>Všetky</button>
          {stores.map((s) => <button key={s.id} onClick={() => setStore(s.id)} data-testid={`warehouse-picker-store-${s.id}`} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${store === s.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>{s.name}</button>)}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative min-w-[180px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Hľadať diel, obj. číslo…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="warehouse-picker-search" />
          </div>
          <div className="space-y-1"><Label className="text-xs">Zoradiť</Label>
            <Select value={sort} onValueChange={pickSort}>
              <SelectTrigger className="w-[200px]" data-testid="warehouse-picker-sort"><SelectValue /></SelectTrigger>
              <SelectContent>{WH_SORTS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`warehouse-picker-sort-${k}`}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Marža %</Label><Input type="number" className="w-24" value={margin} onChange={(e) => setMargin(e.target.value)} data-testid="warehouse-picker-margin" /></div>
          <div className="space-y-1"><Label className="text-xs">Nákupná cena</Label>
            <div className="flex rounded-lg border border-border p-0.5">
              <button type="button" onClick={() => pickMode("net")} className={`rounded px-2 py-1 text-xs font-semibold ${priceMode === "net" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="picker-vat-net">bez DPH</button>
              <button type="button" onClick={() => pickMode("gross")} className={`rounded px-2 py-1 text-xs font-semibold ${priceMode === "gross" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="picker-vat-gross">s DPH</button>
            </div>
          </div>
        </div>
        <div className="mt-2 flex-1 overflow-y-auto rounded-lg border border-border">
          {loading ? (
            <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">Sklad je prázdny alebo nič nevyhovuje hľadaniu.</div>
          ) : (
            <table className="w-full text-sm" data-testid="warehouse-picker-table">
              <thead className="sticky top-0 bg-card text-[11px] uppercase text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="w-10 p-2"></th>
                  <th className="p-2 text-left">Diel</th>
                  <th className="p-2 text-right">Voľné</th>
                  <th className="p-2 text-right">Nákup ({priceMode === "gross" ? "s DPH" : "bez DPH"})</th>
                  <th className="p-2 text-right">Predaj (bez DPH)</th>
                  <th className="p-2 text-right">{"Množstvo"}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const checked = sel[it.id] != null;
                  return (
                    <tr key={it.id} className={`border-b border-border/40 ${checked ? "bg-primary/5" : ""}`} data-testid={`warehouse-picker-row-${it.id}`}>
                      <td className="p-2"><Checkbox checked={checked} disabled={!checked && freeOf(it) <= 0} onCheckedChange={() => toggle(it)} data-testid={`warehouse-picker-check-${it.id}`} /></td>
                      <td className="p-2"><div className="font-medium">{it.name}{it.divisible && <span className="ml-1 rounded bg-sky-500/15 px-1 text-[10px] text-sky-400">L</span>}</div><div className="text-xs text-muted-foreground" data-testid={`warehouse-picker-meta-${it.id}`}>{[it.order_number, it.supplier, it.delivery_date && `Dodané ${String(it.delivery_date).slice(0, 10).split("-").reverse().join(".")}`].filter(Boolean).join(" · ")}</div></td>
                      <td className="p-2 text-right font-mono" data-testid={`warehouse-picker-free-${it.id}`}><span className={freeOf(it) <= 0 ? "text-red-400" : ""}>{freeOf(it).toLocaleString("sk-SK", { maximumFractionDigits: 2 })} {it.unit}</span>{Number(it.reserved_qty) > 0 && <div className="text-[11px] text-amber-400">rezerv. {Number(it.reserved_qty).toLocaleString("sk-SK", { maximumFractionDigits: 2 })}</div>}</td>
                      <td className="p-2 text-right font-mono">{eur(basePrice(it.purchase_price))}{it.divisible ? "/L" : ""}</td>
                      <td className="p-2 text-right font-mono text-primary">{eur(sellPrice(it.purchase_price))}{it.divisible ? "/L" : ""}</td>
                      <td className="p-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button type="button" variant="outline" size="icon" className="h-8 w-8 shrink-0" disabled={!checked} onClick={() => stepQty(it, -1)} aria-label="Znížiť počet" data-testid={`warehouse-picker-qty-dec-${it.id}`}><Minus className="h-3.5 w-3.5" /></Button>
                          <Input type="number" step={it.divisible ? "0.1" : "1"} min="0" max={freeOf(it)} className="h-8 w-16 text-center" value={checked ? sel[it.id] : ""} disabled={!checked} onChange={(e) => setQty(it.id, e.target.value === "" ? "" : String(Math.min(freeOf(it), Math.max(0, Number(e.target.value)))))} placeholder={it.divisible ? "L" : "ks"} data-testid={`warehouse-picker-qty-${it.id}`} />
                          <Button type="button" variant="outline" size="icon" className="h-8 w-8 shrink-0" disabled={!checked} onClick={() => stepQty(it, 1)} aria-label="Zvýšiť počet" data-testid={`warehouse-picker-qty-inc-${it.id}`}><Plus className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Zrušiť</Button>
          <Button onClick={confirm} disabled={!selectedCount} data-testid="warehouse-picker-add">Pridať do zákazky{selectedCount ? ` (${selectedCount})` : ""}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
